/*
 * Copyright © 2016-2020 The Thingsboard Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import './silo-head-space.scss';

/* eslint-disable import/no-unresolved, import/default */

import siloHeadSpaceTemplate from './silo-head-space.tpl.html';

/* eslint-enable import/no-unresolved, import/default */

export default angular.module('agrolog.client.siloHeadSpace', [])
    .directive('agSiloHeadSpace', SiloHeadSpace)
    .name;

/*@ngInject*/
function SiloHeadSpace() {
    return {
        restrict: "E",
        scope: true,
        bindToController: {
            ctx: '='
        },
        controller: SiloHeadSpaceController,
        controllerAs: 'vm',
        templateUrl: siloHeadSpaceTemplate
    };
}

/* eslint-disable angular/angularelement */

/*@ngInject*/
function SiloHeadSpaceController($scope, $translate, $log, types, agTypes, userService, siloService, unitService) {// eslint-disable-line

    let vm = this;

    vm.agTypes = agTypes;
    vm.loading = false;

    vm.temperatureUnits = unitService.temperatureUnitSymbol();
    vm.agTypes = agTypes;

    vm.latestValuesSubscriptionOptions = {
        callbacks: {
            onDataUpdated: onDataUpdated,
            onDataUpdateError: onDataUpdateError,
            dataLoading: (subscription) => {
                vm.loading = subscription.loadingData;
            }
        },
        useDashboardTimewindow: false
    };

    vm.showData = userService.getAuthority() === 'TENANT_ADMIN' || userService.getAuthority() === 'CUSTOMER_USER';
    vm.silo = null;
    vm.headSpaceSensor = null;

    vm.isCo2Supported = false;
    vm.isPressureSupported = false;

    vm.headSpaceTemperatureHistoryLegendConfig = {};
    vm.headSpaceHumidityHistoryLegendConfig = {};
    vm.headSpaceCo2LevelHistoryLegendConfig = {};
    vm.headSpacePressureHistoryLegendConfig = {};

    vm.valueColor = valueColor;

    $scope.$watch('vm.ctx', () => {
        if (vm.ctx && vm.showData) {
            vm.ctx.widgetActions = [];
            vm.ctx.hideTitlePanel = false;

            vm.silo = null;
            vm.headSpaceSensor = null;
            if (vm.ctx.stateController) {
                vm.siloId = vm.ctx.stateController.getStateParams().entityId;
            }
            loadSilo();
            $scope.$watch(() => {
                    return vm.ctx.stateController.getStateParams().entityId;
                },
                (newSiloId, prevSiloId) => {
                    if (newSiloId && !angular.equals(newSiloId, prevSiloId)) {
                        vm.siloId = newSiloId;
                        vm.hideHeadSpace = false;
                        loadSilo();
                    }
                }
            );
        }
    });

    $scope.$watch(() => {
        return vm.silo ? (vm.silo.label || vm.silo.name) : null;
    }, (title) => {
        if (vm.showData && vm.ctx && title) {
            vm.ctx.widgetTitle = $translate.instant('ag.silo.silo-head-space') + ': ' + title;
        }
    });

    function loadSilo() {
        vm.silo = null;
        vm.headSpaceSensor = null;
        reset();
        if (vm.siloId) {
            vm.loading = true;
            siloService.getSilo(vm.siloId).then(
                (silo) => {
                    vm.silo = silo;
                    siloService.getSiloHeadSpaceSensor(vm.siloId).then(
                        (headSpaceSensor) => {
                            vm.headSpaceSensor = headSpaceSensor[0];
                            if (vm.headSpaceSensor) {
                                var latestValuesSubscriptionsInfo = [{
                                    type: types.datasourceType.entity,
                                    entityType: types.entityType.device,
                                    entityId: vm.headSpaceSensor.id.id,
                                    entityName: vm.headSpaceSensor.name,
                                    timeseries: [
                                        {name: vm.agTypes.telemetry.headSpaceSensor.temperature},
                                        {name: vm.agTypes.telemetry.headSpaceSensor.humidity},
                                        {name: vm.agTypes.telemetry.headSpaceSensor.dewpoint},
                                        {name: vm.agTypes.telemetry.headSpaceSensor.co2level},
                                        {name: vm.agTypes.telemetry.headSpaceSensor.pressure}
                                    ],
                                    attributes: [
                                        { name: agTypes.attributes.headSpaceSensor.deviceType, label: 'Headspace Device Type'}
                                    ]
                                }];
                                vm.latestValuesSubscriptionOptions.timeWindowConfig = angular.copy(vm.ctx.dashboardTimewindow);
                                vm.latestValuesSubscriptionOptions.timeWindowConfig.aggregation.type = types.aggregation.none.value;
                                vm.latestValuesSubscriptionOptions.timeWindowConfig.aggregation.limit = 1;
                                vm.ctx.subscriptionApi.createSubscriptionFromInfo (
                                    types.widgetType.timeseries.value, latestValuesSubscriptionsInfo, vm.latestValuesSubscriptionOptions, false, true).then(
                                    (subscription) => {
                                        vm.latestValuesSubscription = subscription;
                                    }
                                );

                                vm.headSpaceTemperatureSubscriptionsInfo = angular.copy(latestValuesSubscriptionsInfo);
                                vm.headSpaceTemperatureSubscriptionsInfo[0].timeseries = [
                                    {
                                        name: agTypes.telemetry.headSpaceSensor.temperature,
                                        label: $translate.instant('ag.silo.head-space-temperature'),
                                        color: agTypes.colors.maxColor,
                                        postFuncBody: unitService.temperaturePostprocessingFuncBody()
                                    },
                                    {
                                        name: agTypes.telemetry.headSpaceSensor.dewpoint,
                                        label: $translate.instant('ag.silo.head-space-dewpoint'),
                                        color: agTypes.colors.avgColor,
                                        postFuncBody: unitService.temperaturePostprocessingFuncBody()
                                    }
                                ];

                                vm.headSpaceHumiditySubscriptionsInfo = angular.copy(latestValuesSubscriptionsInfo);
                                vm.headSpaceHumiditySubscriptionsInfo[0].timeseries = [
                                    {
                                        name: agTypes.telemetry.headSpaceSensor.humidity,
                                        label: $translate.instant('ag.silo.head-space-humidity'),
                                        color: agTypes.colors.minColor,
                                    }
                                ];

                                vm.headSpaceCo2LevelSubscriptionsInfo = angular.copy(latestValuesSubscriptionsInfo);
                                vm.headSpaceCo2LevelSubscriptionsInfo[0].timeseries = [
                                    {
                                        name: agTypes.telemetry.headSpaceSensor.co2level,
                                        label: $translate.instant('ag.silo.head-space-co2level'),
                                        color: agTypes.colors.mainGrey,
                                    }
                                ];

                                vm.headSpacePressureSubscriptionsInfo = angular.copy(latestValuesSubscriptionsInfo);
                                vm.headSpacePressureSubscriptionsInfo[0].timeseries = [
                                    {
                                        name: agTypes.telemetry.headSpaceSensor.pressure,
                                        label: $translate.instant('ag.silo.head-space-pressure'),
                                        color: agTypes.colors.maxColor,
                                    }
                                ];
                            } else {
                                vm.hideHeadSpace = true;
                                vm.loading = false;
                            }
                        },
                        () => {
                            vm.loading = false;
                        }
                    );
                },
                () => {
                    vm.loading = false;
                }
            );
        }
    }

    function reset() {
        if (vm.latestValuesSubscription) {
            vm.ctx.subscriptionApi.removeSubscription(vm.latestValuesSubscription.id);
        }
        resetValues();
    }

    function resetValues() {
        vm.tempVal = "";
        vm.humidityVal = "";
        vm.dewpVal = "";
        vm.co2Val = "";
        vm.pressVal = "";
    }

    function onDataUpdated(subscription, apply) {
        var data = subscription.data;
        for (var i = 0; i < data.length; i++) {
            var datasourceData = data[i];
            if (datasourceData.dataKey.name == agTypes.attributes.headSpaceSensor.deviceType) {
                var deviceType = getValue(datasourceData);
                if (deviceType !== null) {
                    var isCableHSS = deviceType === agTypes.headSpaceSensorTypes.hs2000;
                    vm.isCo2Supported = !isCableHSS;
                    vm.isPressureSupported = !isCableHSS
                }
                continue                
            }
            var val = getNumberValue(datasourceData);
            switch (datasourceData.dataKey.name) {
                case vm.agTypes.telemetry.headSpaceSensor.temperature:
                    vm.tempVal = vm.ctx.utils.formatValue(unitService.toUserTemperature(val), 1, '') + '°';
                    break;
                case vm.agTypes.telemetry.headSpaceSensor.dewpoint:
                    vm.dewpVal = vm.ctx.utils.formatValue(unitService.toUserTemperature(val), 1, '') + '°';
                    break;
                case vm.agTypes.telemetry.headSpaceSensor.humidity:
                    vm.humidityVal = vm.ctx.utils.formatValue(val, 1, '' ) + agTypes.units.humidity;
                    break;
                case vm.agTypes.telemetry.headSpaceSensor.co2level:
                    vm.co2Val = vm.ctx.utils.formatValue(val, 0, '' ) + agTypes.units.co2Level;
                    break;
                case vm.agTypes.telemetry.headSpaceSensor.pressure:
                    vm.pressVal = vm.ctx.utils.formatValue(val, 1, '' ) + agTypes.units.pressure;
                    break;
            }
        }
        if (apply) {
            $scope.$digest();
        }
    }

    function onDataUpdateError(subscription, e) {
        $log.error(e);
    }
    function valueColor(/*val*/) {
        return agTypes.colors.normalGreyColor;
    }

    function getValue (keyData) {
        if (keyData && keyData.data && keyData.data[0] && angular.isDefined(keyData.data[0][1])) {
            return keyData.data[0][1];            
        } else {
            return null;
        }
    }

    function getNumberValue(keyData) {
        if (keyData && keyData.data && keyData.data[0] && angular.isDefined(keyData.data[0][1])) {
            if (!isNaN(keyData.data[0][1])) {
                return Number(keyData.data[0][1]);
            } else {
                return 0;
            }
        } else {
            return 0;
        }
    }
}